import type {
  PartnerAccessState,
  PartnerChannelState,
  PartnerPhase,
  PartnerReleaseState,
  PartnerReportSource,
  PartnerReportStatus,
  PartnerTier,
  PartnerWorldCompat,
} from '../types/partners';

/**
 * Words and badge tones for every Partner Hub state, in one place so the
 * partner page, the inbox, the admin tab and the dashboard say the same
 * thing. Each entry carries a `hint`: the sentence a `title` shows, saying
 * what the state means and, where it matters, what to do about it.
 */
export type BadgeTone = 'neutral' | 'accent' | 'purple' | 'success' | 'warning' | 'amber' | 'orange' | 'danger' | 'info';

export interface StatusLabel {
  label: string;
  tone: BadgeTone;
  hint: string;
}

/** `badge badge-success`, for a label's tone. */
export const badgeClass = (tone: BadgeTone): string => `badge badge-${tone}`;

const unknown = (value: string | null | undefined, hint: string): StatusLabel => ({
  label: value ? value.replace(/_/g, ' ') : 'Unknown',
  tone: 'neutral',
  hint,
});

// ── Tier ───────────────────────────────────────────────────────────────────

const TIERS: Record<PartnerTier, StatusLabel> = {
  developer: {
    label: 'Partnered Developer',
    tone: 'purple',
    hint: 'Gets the Partnered Modpack Developer role, which opens the stage, and may share server packs.',
  },
  contributor: {
    label: 'Contributor',
    tone: 'info',
    hint: 'Gets the contributor acknowledgement role and access to the partner channel.',
  },
};

export const tierLabel = (tier: string | null | undefined): StatusLabel =>
  TIERS[tier as PartnerTier] ?? unknown(tier, 'Not a tier Argus knows.');

// ── Phase ──────────────────────────────────────────────────────────────────

const PHASES: Record<PartnerPhase, StatusLabel> = {
  active: {
    label: 'Active partner',
    tone: 'success',
    hint: 'A partnership is active, paused or on hold, so the team has the channel and their roles.',
  },
  early: {
    label: 'Channel open early',
    tone: 'info',
    hint: 'No partnership is active yet, but staff opened the channel early. The team has access, but no roles.',
  },
  archived: {
    label: 'Archived',
    tone: 'neutral',
    hint: 'Every partnership has ended, so the channel is archived and the team’s access and roles are removed.',
  },
  none: {
    label: 'Not started',
    tone: 'neutral',
    hint: 'No partnership is active yet, so nothing is set up in Discord.',
  },
};

export const phaseLabel = (phase: string | null | undefined): StatusLabel =>
  PHASES[phase as PartnerPhase] ?? unknown(phase, 'Argus could not work out this partner’s phase.');

// ── Member access ──────────────────────────────────────────────────────────

const ACCESS: Record<PartnerAccessState, StatusLabel> = {
  ok: { label: 'Has access', tone: 'success', hint: 'Mr. Roboto has given them channel access and the role their tier earns.' },
  pending: { label: 'Waiting', tone: 'warning', hint: 'Mr. Roboto has not applied the latest change for them yet. It usually takes under a minute.' },
  not_in_server: {
    label: 'Not in the server',
    tone: 'amber',
    hint: 'They are not in the Discord server. Access and roles are applied as soon as they join.',
  },
  suspended: {
    label: 'Suspended',
    tone: 'danger',
    hint: 'They are banished or suspended, so Mr. Roboto leaves their access and roles alone.',
  },
  held: {
    label: 'Removal held',
    tone: 'orange',
    hint: 'A removal is held back, by setup mode or the safety cap on how many removals one pass may make. A PR admin can apply it from the Discord section.',
  },
  failed: { label: 'Failed', tone: 'danger', hint: 'Mr. Roboto could not apply this. The detail says why.' },
  revoked: { label: 'Removed', tone: 'neutral', hint: 'Their channel access and partner role have been taken away.' },
};

export const accessLabel = (state: string | null | undefined): StatusLabel =>
  state
    ? ACCESS[state as PartnerAccessState] ?? unknown(state, 'Mr. Roboto reported a state Argus does not know.')
    : { label: 'Not synced yet', tone: 'neutral', hint: 'Mr. Roboto has not reported on this person yet. That happens once Discord is managed for this partner.' };

/** Access states that need someone to look: neither fine nor on its way. */
export const isAccessIssue = (state: string | null | undefined): boolean =>
  !!state && state !== 'ok' && state !== 'pending' && state !== 'revoked';

// ── Channel ────────────────────────────────────────────────────────────────

const CHANNELS: Record<PartnerChannelState, StatusLabel> = {
  active: { label: 'Channel active', tone: 'success', hint: 'The channel is in a partner category and the team has access.' },
  archived: { label: 'Channel archived', tone: 'neutral', hint: 'The channel is in the staff-only archive category.' },
  missing: {
    label: 'Channel missing',
    tone: 'danger',
    hint: 'The linked channel no longer exists. Mr. Roboto never recreates it by itself: unlink it and choose to create a new one.',
  },
  misplaced: {
    label: 'Channel misplaced',
    tone: 'warning',
    hint: 'The channel is outside the partner and archive categories. Mr. Roboto reports this rather than moving it back.',
  },
  none: { label: 'No channel', tone: 'neutral', hint: 'This partner has no channel yet.' },
  error: { label: 'Channel error', tone: 'danger', hint: 'Mr. Roboto hit an error on the last pass. The last error below says what went wrong.' },
};

export const channelLabel = (state: string | null | undefined): StatusLabel =>
  state
    ? CHANNELS[state as PartnerChannelState] ?? unknown(state, 'Mr. Roboto reported a state Argus does not know.')
    : { label: 'Not synced', tone: 'neutral', hint: 'Mr. Roboto has not reported on this channel yet.' };

// ── Update reports ─────────────────────────────────────────────────────────

const REPORT_STATUSES: Record<PartnerReportStatus, StatusLabel> = {
  new: { label: 'New', tone: 'info', hint: 'Nobody on staff has picked this up yet.' },
  acknowledged: { label: 'Acknowledged', tone: 'accent', hint: 'Staff have seen it. The partner sees that too.' },
  scheduled: { label: 'Scheduled', tone: 'purple', hint: 'The update is planned for our servers.' },
  live: { label: 'Live', tone: 'success', hint: 'Our servers run this version.' },
  dismissed: { label: 'Dismissed', tone: 'neutral', hint: 'Staff decided not to act on this report.' },
  superseded: { label: 'Superseded', tone: 'neutral', hint: 'A later report for the same version replaced this one.' },
};

export const reportStatusLabel = (status: string | null | undefined): StatusLabel =>
  REPORT_STATUSES[status as PartnerReportStatus] ?? unknown(status, 'Not a status Argus knows.');

export const isOpenReportStatus = (status: string | null | undefined): boolean =>
  status === 'new' || status === 'acknowledged' || status === 'scheduled';

const SOURCES: Record<PartnerReportSource, StatusLabel> = {
  partner: { label: 'Partner', tone: 'accent', hint: 'The partner reported this from their channel.' },
  staff: { label: 'Staff', tone: 'neutral', hint: 'Logged by staff on the partner’s behalf.' },
  curseforge: {
    label: 'Found on CurseForge, not reported',
    tone: 'orange',
    hint: 'Argus saw this version on CurseForge before the partner reported it. Only staff see these.',
  },
};

export const reportSourceLabel = (source: string | null | undefined): StatusLabel =>
  SOURCES[source as PartnerReportSource] ?? unknown(source, 'Not a source Argus knows.');

const RELEASES: Record<PartnerReleaseState, string> = { released: 'Out now', upcoming: 'Coming soon' };
export const releaseStateLabel = (state: string | null | undefined): string => RELEASES[state as PartnerReleaseState] ?? (state || 'Unknown');

const WORLDS: Record<PartnerWorldCompat, string> = {
  compatible: 'Existing worlds keep working',
  new_world: 'Needs a new world',
  unsure: 'Not sure about worlds',
};
export const worldCompatLabel = (compat: string | null | undefined): string => WORLDS[compat as PartnerWorldCompat] ?? (compat || 'Unknown');

// ── Server packs (Tesseract file transfers) ────────────────────────────────

const FILE_STATES: Record<string, StatusLabel> = {
  available: { label: 'Available', tone: 'success', hint: 'Staff can download it with the link.' },
  exhausted: { label: 'Download limit reached', tone: 'warning', hint: 'It has been downloaded as many times as the partner allowed.' },
  expired: { label: 'Expired', tone: 'neutral', hint: 'The download link has run out. Ask the partner to share it again if it is still needed.' },
  revoked: { label: 'Revoked', tone: 'danger', hint: 'Staff took the download link down.' },
  deleted: { label: 'Deleted', tone: 'neutral', hint: 'The file has been deleted from storage.' },
};

/** A shared file's state, counting an available file past its expiry as expired (Tesseract stores the raw state). */
export function serverPackFileLabel(state: string | null | undefined, expiresAt: number, now: number): StatusLabel {
  const effective = state === 'available' && expiresAt <= now ? 'expired' : state;
  return (effective && FILE_STATES[effective]) || unknown(state, 'Tesseract reported a state Argus does not know.');
}

const LINK_STATES: Record<string, StatusLabel> = {
  pending: { label: 'Waiting for upload', tone: 'info', hint: 'The partner has the link but has not opened it yet.' },
  unlocked: { label: 'Upload started', tone: 'accent', hint: 'The partner has opened the link and may be uploading now.' },
};

export const serverPackLinkLabel = (state: string | null | undefined): StatusLabel =>
  (state && LINK_STATES[state]) || unknown(state, 'Tesseract reported a state Argus does not know.');
